import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Separator } from "../../components/ui/separator";
import { LOAN_PROGRAMS } from "../../lib/constants";
import { Upload, X, FileText, Plus } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPE_OPTIONS = [
  { key: "executed_contract", label: "Executed Contract (property purchase)" },
  { key: "drivers_license", label: "Driver's License / Government ID" },
  { key: "credit_report", label: "Credit Report" },
  { key: "bank_statement", label: "Bank Statements" },
  { key: "other", label: "Custom / Other" },
];
const DOC_TYPE_LABEL = Object.fromEntries(DOC_TYPE_OPTIONS.map((d) => [d.key, d.label]));
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.heic,.heif";

export default function SponsorSubmit() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [brokers, setBrokers] = useState([]);
  const [attributeBrokerId, setAttributeBrokerId] = useState("");
  const [form, setForm] = useState({
    borrower_name: "", borrower_email: "", borrower_phone: "",
    credit_score: 0, has_re_experience: false, experience_type: "", num_projects: 0,
    owns_home: false, property_in_llc: false, llc_name: "", capital_available: 0,
    property_address: "", beds: 0, baths: 0, living_sqft: 0,
    property_type: "single_family", has_clean_title: true,
    loan_type: "fix_and_flip", purchase_price: 0, expected_loan_amount: 0,
    estimated_rehab_budget: 0, arv: 0, notes: "",
  });

  // Admin: fetch broker list once so it can attribute the app
  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/brokers");
        setBrokers(data || []);
      } catch (e) {
        console.error("Failed to load brokers for attribution:", e);
      }
    })();
  }, [isAdmin]);

  // Files broker is uploading right now
  const [pendingFiles, setPendingFiles] = useState([]); // {file, doc_type, label}
  // Docs broker wants to request from borrower (auto-included in invite email)
  const [requestedDocs, setRequestedDocs] = useState([]); // {doc_type, label}

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addFiles = (e) => {
    const fl = Array.from(e.target.files || []);
    if (!fl.length) return;
    const oversize = fl.filter((f) => f.size > 15 * 1024 * 1024);
    if (oversize.length) { toast.error("Files must be 15MB or smaller"); return; }
    setPendingFiles((prev) => [
      ...prev,
      ...fl.map((f) => ({ uid: crypto.randomUUID(), file: f, doc_type: "executed_contract", label: "" })),
    ]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const updateFile = (i, k, v) => {
    setPendingFiles((prev) => prev.map((p, idx) => (idx === i ? { ...p, [k]: v } : p)));
  };
  const removeFile = (i) => setPendingFiles((prev) => prev.filter((_, idx) => idx !== i));

  const toggleRequested = (doc_type) => {
    setRequestedDocs((prev) => {
      const exists = prev.find((r) => r.doc_type === doc_type && doc_type !== "other");
      if (exists) return prev.filter((r) => r.doc_type !== doc_type);
      return [...prev, { uid: crypto.randomUUID(), doc_type, label: "" }];
    });
  };
  const updateRequested = (i, k, v) => {
    setRequestedDocs((prev) => prev.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  };
  const removeRequested = (i) => setRequestedDocs((prev) => prev.filter((_, idx) => idx !== i));
  const addCustomRequested = () => setRequestedDocs((prev) => [...prev, { uid: crypto.randomUUID(), doc_type: "other", label: "" }]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.borrower_name || !form.borrower_email || !form.property_address) {
      toast.error("Borrower name, email, and property address are required");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      [
        "credit_score","num_projects","capital_available","beds","baths","living_sqft",
        "purchase_price","expected_loan_amount","estimated_rehab_budget","arv",
      ].forEach((k) => { payload[k] = Number(payload[k] || 0); });
      payload.requested_docs = requestedDocs.filter((r) => r.doc_type);
      if (isAdmin && attributeBrokerId) payload.attribute_to_sponsor_id = attributeBrokerId;
      const { data } = await api.post("/sponsor/applications", payload);

      // Upload broker-supplied files in sequence
      let uploaded = 0;
      for (const p of pendingFiles) {
        try {
          const fd = new FormData();
          fd.append("doc_type", p.doc_type);
          fd.append("file", p.file);
          if ((p.label || "").trim()) fd.append("label", p.label.trim());
          await api.post(`/documents/application/${data.application_id}`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          uploaded += 1;
        } catch (err) {
          toast.error(`Upload failed for ${p.file.name}: ${errorMessage(err)}`);
        }
      }
      const upMsg = uploaded ? ` · ${uploaded} doc${uploaded > 1 ? "s" : ""} attached` : "";
      toast.success(`Application submitted · AI Score ${data.ai_score.total} · ${data.ai_score.category}${upMsg}`);
      nav(isAdmin
        ? `/admin/applications/${data.application_id}`
        : `/broker/applications/${data.application_id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Submit Application</h1>
        <p className="mt-1 text-sm text-slate-400">
          {isAdmin
            ? "Enter an application directly. You can optionally attribute it to a broker below."
            : "Submit an application on behalf of a borrower."}
        </p>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="submit-app-form">
        {isAdmin && (
          <Card className="border-slate-800 bg-slate-900 p-6" data-testid="admin-attribution-card">
            <SectionTitle>Attribution</SectionTitle>
            <p className="-mt-2 mb-4 text-xs text-slate-400">
              Optional — attribute this application to one of your brokers. Leave blank for a direct admin submission.
            </p>
            <div className="max-w-sm">
              <Label className="text-slate-300 text-xs uppercase tracking-[0.15em]">Attribute to broker</Label>
              <Select value={attributeBrokerId || "none"} onValueChange={(v) => setAttributeBrokerId(v === "none" ? "" : v)}>
                <SelectTrigger className="mt-1.5 border-slate-800 bg-slate-950 text-slate-50" data-testid="admin-broker-select">
                  <SelectValue placeholder="None (direct admin submission)" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  <SelectItem value="none">None — direct admin submission</SelectItem>
                  {brokers.map((b) => (
                    <SelectItem key={b.user_id} value={b.user_id}>
                      {b.name} · {b.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </Card>
        )}
        <Card className="border-slate-800 bg-slate-900 p-6">
          <SectionTitle>Borrower Information</SectionTitle>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <F label="Full Name" span={2}><Input value={form.borrower_name} onChange={(e) => set("borrower_name", e.target.value)} className={INP} data-testid="sub-borrower-name" /></F>
            <F label="Email"><Input type="email" value={form.borrower_email} onChange={(e) => set("borrower_email", e.target.value)} className={INP} data-testid="sub-borrower-email" /></F>
            <F label="Phone"><Input value={form.borrower_phone} onChange={(e) => set("borrower_phone", e.target.value)} className={INP} data-testid="sub-borrower-phone" /></F>
          </div>
        </Card>

        <Card className="border-slate-800 bg-slate-900 p-6">
          <SectionTitle>Experience & Capital</SectionTitle>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <F label="Credit Score"><Input type="number" value={form.credit_score} onChange={(e) => set("credit_score", e.target.value)} className={INP} data-testid="sub-credit" /></F>
            <F label="Available Capital ($)"><Input type="number" value={form.capital_available} onChange={(e) => set("capital_available", e.target.value)} className={INP} data-testid="sub-capital" /></F>
            <div className="sm:col-span-2 flex items-center gap-2">
              <Checkbox checked={form.has_re_experience} onCheckedChange={(v) => set("has_re_experience", !!v)} className={CB} data-testid="sub-has-exp" />
              <Label className="text-slate-300 cursor-pointer" onClick={() => set("has_re_experience", !form.has_re_experience)}>Borrower has RE investment experience</Label>
            </div>
            <F label="Experience Type">
              <Select value={form.experience_type} onValueChange={(v) => set("experience_type", v)}>
                <SelectTrigger className={INP} data-testid="sub-exp-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  <SelectItem value="fix_and_flip">Fix & Flip</SelectItem>
                  <SelectItem value="rental">Rental / Buy & Hold</SelectItem>
                  <SelectItem value="ground_up">Ground-Up</SelectItem>
                  <SelectItem value="wholesale">Wholesaling</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Projects (last 36 months)"><Input type="number" value={form.num_projects} onChange={(e) => set("num_projects", e.target.value)} className={INP} data-testid="sub-projects" /></F>
            <div className="sm:col-span-2 flex items-center gap-2">
              <Checkbox checked={form.owns_home} onCheckedChange={(v) => set("owns_home", !!v)} className={CB} data-testid="sub-owns-home" />
              <Label className="text-slate-300 cursor-pointer" onClick={() => set("owns_home", !form.owns_home)}>Owns primary residence</Label>
            </div>
            <div className="sm:col-span-2 flex items-center gap-2">
              <Checkbox checked={form.property_in_llc} onCheckedChange={(v) => set("property_in_llc", !!v)} className={CB} data-testid="sub-in-llc" />
              <Label className="text-slate-300 cursor-pointer" onClick={() => set("property_in_llc", !form.property_in_llc)}>Property held in LLC</Label>
            </div>
            <F label="LLC Name" span={2}><Input value={form.llc_name} onChange={(e) => set("llc_name", e.target.value)} className={INP} data-testid="sub-llc" /></F>
          </div>
        </Card>

        <Card className="border-slate-800 bg-slate-900 p-6">
          <SectionTitle>Property</SectionTitle>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <F label="Property Address" span={2}><Input value={form.property_address} onChange={(e) => set("property_address", e.target.value)} className={INP} data-testid="sub-address" /></F>
            <F label="Property Type">
              <Select value={form.property_type} onValueChange={(v) => set("property_type", v)}>
                <SelectTrigger className={INP} data-testid="sub-ptype"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  <SelectItem value="single_family">Single Family</SelectItem>
                  <SelectItem value="multifamily">Multifamily</SelectItem>
                  <SelectItem value="condo">Condo</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="land">Land</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Living Sq Ft"><Input type="number" value={form.living_sqft} onChange={(e) => set("living_sqft", e.target.value)} className={INP} data-testid="sub-sqft" /></F>
            <F label="Beds"><Input type="number" value={form.beds} onChange={(e) => set("beds", e.target.value)} className={INP} data-testid="sub-beds" /></F>
            <F label="Baths"><Input type="number" step="0.5" value={form.baths} onChange={(e) => set("baths", e.target.value)} className={INP} data-testid="sub-baths" /></F>
            <div className="sm:col-span-2 flex items-center gap-2">
              <Checkbox checked={form.has_clean_title} onCheckedChange={(v) => set("has_clean_title", !!v)} className={CB} data-testid="sub-clean" />
              <Label className="text-slate-300 cursor-pointer" onClick={() => set("has_clean_title", !form.has_clean_title)}>Clean title</Label>
            </div>
          </div>
        </Card>

        <Card className="border-slate-800 bg-slate-900 p-6">
          <SectionTitle>Financial</SectionTitle>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <F label="Loan Type">
              <Select value={form.loan_type} onValueChange={(v) => set("loan_type", v)}>
                <SelectTrigger className={INP} data-testid="sub-loan-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                  {LOAN_PROGRAMS.map((p) => <SelectItem key={p.key} value={p.key}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Purchase Price ($)"><Input type="number" value={form.purchase_price} onChange={(e) => set("purchase_price", e.target.value)} className={INP} data-testid="sub-purchase" /></F>
            <F label="Expected Loan Amount ($)"><Input type="number" value={form.expected_loan_amount} onChange={(e) => set("expected_loan_amount", e.target.value)} className={INP} data-testid="sub-loan-amt" /></F>
            <F label="Rehab Budget ($)"><Input type="number" value={form.estimated_rehab_budget} onChange={(e) => set("estimated_rehab_budget", e.target.value)} className={INP} data-testid="sub-rehab" /></F>
            <F label="ARV ($)"><Input type="number" value={form.arv} onChange={(e) => set("arv", e.target.value)} className={INP} data-testid="sub-arv" /></F>
          </div>
          <Separator className="my-6 bg-slate-800" />
          <F label="Notes" span={2}><Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className={`${INP} min-h-[100px]`} data-testid="sub-notes" /></F>
        </Card>

        <Card className="border-slate-800 bg-slate-900 p-6">
          <SectionTitle>Documents</SectionTitle>
          <p className="-mt-3 mb-5 text-xs text-slate-400">
            Upload any documents you already have, and tick what you&apos;d like the borrower to provide.
            Typical asks: executed contract for the property, driver&apos;s license, credit report, and bank statements.
            The borrower will receive an email with a secure link to log in and upload these.
          </p>

          {/* === Upload now === */}
          <div className="mb-6">
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Upload now (on behalf of borrower)</Label>
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept={ACCEPT}
                  onChange={addFiles}
                  className="hidden"
                  data-testid="sub-doc-files"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  className="border-slate-700 bg-slate-950 text-slate-100 hover:bg-slate-800"
                  data-testid="sub-add-files-btn"
                >
                  <Upload className="mr-2 h-3 w-3" /> Add files
                </Button>
              </div>
            </div>
            {pendingFiles.length === 0 ? (
              <div className="rounded-md border border-dashed border-slate-800 p-5 text-center text-xs text-slate-500">
                No files yet. PDF, PNG, JPG, HEIC · 15MB max each.
              </div>
            ) : (
              <ul className="space-y-2">
                {pendingFiles.map((p, i) => (
                  <li
                    key={p.uid}
                    className="grid grid-cols-1 gap-2 rounded-md border border-slate-800 bg-slate-950 p-3 sm:grid-cols-[auto_1fr_180px_auto] sm:items-center"
                    data-testid={`sub-pf-row-${i}`}
                  >
                    <FileText className="h-4 w-4 text-emerald-400" />
                    <div className="min-w-0">
                      <div className="truncate text-sm text-slate-100">{p.file.name}</div>
                      <div className="text-[11px] text-slate-500">{(p.file.size / 1024).toFixed(1)} KB</div>
                      <input
                        type="text"
                        placeholder={`Name (e.g. "John's contract - 123 Main")`}
                        value={p.label}
                        onChange={(e) => updateFile(i, "label", e.target.value)}
                        className="mt-2 w-full rounded border border-slate-800 bg-slate-900 px-2 py-1 text-xs text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                        data-testid={`sub-pf-label-${i}`}
                      />
                    </div>
                    <Select value={p.doc_type} onValueChange={(v) => updateFile(i, "doc_type", v)}>
                      <SelectTrigger className="border-slate-800 bg-slate-900 text-slate-100 text-xs" data-testid={`sub-pf-type-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-800 text-slate-100">
                        {DOC_TYPE_OPTIONS.map((d) => (
                          <SelectItem key={d.key} value={d.key}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-rose-300 hover:text-rose-200"
                      data-testid={`sub-pf-remove-${i}`}
                      aria-label="Remove"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <Separator className="my-4 bg-slate-800" />

          {/* === Request from borrower === */}
          <div>
            <Label className="text-slate-300 text-xs uppercase tracking-widest">Request from borrower</Label>
            <p className="mt-1 mb-3 text-[11px] text-slate-500">
              Tick the docs you want the borrower to upload. They&apos;ll see this list in their invite email.
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {DOC_TYPE_OPTIONS.filter((d) => d.key !== "other").map((d) => {
                const checked = !!requestedDocs.find((r) => r.doc_type === d.key);
                return (
                  <label
                    key={d.key}
                    className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm transition-colors ${
                      checked
                        ? "border-emerald-500/40 bg-emerald-500/5 text-slate-100"
                        : "border-slate-800 bg-slate-950 text-slate-300 hover:border-slate-700"
                    }`}
                    data-testid={`sub-req-${d.key}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggleRequested(d.key)}
                      className={CB}
                    />
                    {d.label}
                  </label>
                );
              })}
            </div>

            {/* Custom requested items */}
            {requestedDocs.filter((r) => r.doc_type === "other").length > 0 && (
              <ul className="mt-3 space-y-2">
                {requestedDocs.map((r, i) => r.doc_type === "other" && (
                  <li key={r.uid} className="flex items-center gap-2 rounded-md border border-slate-800 bg-slate-950 p-2" data-testid={`sub-req-custom-${i}`}>
                    <input
                      type="text"
                      value={r.label}
                      onChange={(e) => updateRequested(i, "label", e.target.value)}
                      placeholder="Custom document name (e.g. LLC operating agreement)"
                      className="flex-1 rounded border border-slate-800 bg-slate-900 px-2 py-1.5 text-xs text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                    />
                    <button type="button" onClick={() => removeRequested(i)} className="text-rose-300 hover:text-rose-200" aria-label="Remove">
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={addCustomRequested}
              className="mt-3 inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200"
              data-testid="sub-req-add-custom"
            >
              <Plus className="h-3 w-3" /> Add custom request
            </button>
          </div>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={busy} size="lg" className="bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60" data-testid="sub-submit-btn">
            {busy ? "Submitting…" : "Submit Application"}
          </Button>
        </div>
      </form>
    </div>
  );
}

const INP = "border-slate-800 bg-slate-950 text-slate-50 focus-visible:ring-emerald-500/40";
const CB = "border-slate-700 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600";

function SectionTitle({ children }) {
  return <div className="mb-5 text-sm font-semibold text-slate-100">{children}</div>;
}
function F({ label, children, span = 1 }) {
  return (
    <div className={span === 2 ? "sm:col-span-2" : ""}>
      <Label className="text-slate-300">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
